import { useMemo } from "react";

import type { CycleLabel } from "../api/client";
import type { SessionSummary } from "../api/client";

/**
 * Derives the kaimahi's allowed actions for assessment submission from
 * the whānau's session history. Server enforces the same rules in the
 * POST /assessment-sessions handler — this client-side derivation just
 * keeps the UX honest (disable buttons, filter cycle options, show
 * banners) instead of letting kaimahi hit errors at submit time.
 *
 * Rules:
 *   • Any session with signoff_status = 'pending' OR 'returned' blocks
 *     ALL new submissions for the whānau until resolved.
 *   • First session ever for the whānau must be cycle 'entry'.
 *   • After any 'entry' session has been attempted (regardless of
 *     approval state), no further entries can be submitted.
 */

export type SubmissionState = {
  /** True when the kaimahi may start a new wizard session. */
  canSubmit: boolean;
  /** Blocking signoff (if any) — describes why canSubmit is false. */
  blockingSignoff: {
    status: "pending" | "returned";
    cycleLabel: string;
    sessionId: number;
  } | null;
  /** Cycles the kaimahi is permitted to pick in the wizard. */
  allowedCycles: readonly CycleLabel[];
  /**
   * The cycle the wizard should default to. 'entry' for the first-ever
   * session, otherwise 'midpoint' as a reasonable next step.
   */
  defaultCycle: CycleLabel;
  /** True when this would be the first-ever session for the whānau. */
  isFirstSession: boolean;
};

const ALL_CYCLES: readonly CycleLabel[] = [
  "entry",
  "midpoint",
  "exit",
  "follow-up",
];

export function deriveSubmissionState(
  sessions: SessionSummary[],
): SubmissionState {
  // Find a blocking signoff — pending or returned.
  const blocking = sessions.find(
    (s) => s.signoff_status === "pending" || s.signoff_status === "returned",
  );
  const blockingSignoff = blocking
    ? {
        status: blocking.signoff_status as "pending" | "returned",
        cycleLabel: blocking.cycle_label,
        sessionId: blocking.session_id,
      }
    : null;

  const entryAttempted = sessions.some((s) => s.cycle_label === "entry");
  const isFirstSession = sessions.length === 0;

  // Permitted cycles depending on entry-attempted state.
  let allowedCycles: readonly CycleLabel[];
  if (isFirstSession) {
    // Brand new whānau — entry only.
    allowedCycles = ["entry"];
  } else if (entryAttempted) {
    // Entry already done — exclude it from the picker.
    allowedCycles = ALL_CYCLES.filter((c) => c !== "entry");
  } else {
    // Edge case: there are sessions but none are 'entry'. Shouldn't
    // happen in normal flow (the server rejects non-entry-first), but
    // hand it back so the UI doesn't trap. Allow entry only.
    allowedCycles = ["entry"];
  }

  const defaultCycle: CycleLabel = isFirstSession ? "entry" : "midpoint";

  return {
    canSubmit: blockingSignoff === null,
    blockingSignoff,
    allowedCycles,
    defaultCycle,
    isFirstSession,
  };
}

export function useSubmissionState(
  sessions: SessionSummary[],
): SubmissionState {
  return useMemo(() => deriveSubmissionState(sessions), [sessions]);
}
